import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { downgradeComponent } from '@angular/upgrade/static';
import App from '@app/app';
import type { ResolvedImage } from '@app/interface/image/resolved-image.interface';

@Component({
  selector: 'lt-resolved-image',
  templateUrl: './lt-resolved-image.component.html',
  styleUrl: './lt-resolved-image.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LtResolvedImageComponent {
  /** Resolved image data. `undefined` triggers the fallback path. */
  resolved = input<ResolvedImage | undefined>(undefined);
  /** Required for automation (Playwright/Selenium). */
  testId = input.required<string>();
  /** Used when `resolved.altText` is missing. Empty string = decorative. */
  altFallback = input<string>('');
  /** Shown when `resolved` is undefined OR the inner `<img>` errors. */
  fallbackSrc = input<string | undefined>(undefined);
  /** `lazy` (default) or `eager` for above-the-fold critical images. */
  loading = input<'lazy' | 'eager'>('lazy');
  /** `object-fit` for the `<img>`. Only takes effect when `fill` is set. */
  fit = input<'fill' | 'contain' | 'cover' | 'none' | 'scale-down'>('cover');
  /** Focal point when cropping: `center` (default) or `top` (bias toward the top, e.g. faces in portraits). Pairs with `fit` when `fill` is set. */
  focus = input<'center' | 'top'>('center');
  /** When `true`, the `<img>` fills its container (100%×100%) so `fit` applies; otherwise natural size. */
  fill = input<boolean>(false);

  protected readonly errored = signal(false);
  protected readonly isTopFocused = computed(() => this.focus() === 'top');

  protected readonly displaySrc = computed<string | undefined>(() => {
    if (this.errored()) {
      return this.fallbackSrc();
    }
    return this.resolved()?.src ?? this.fallbackSrc();
  });

  protected readonly displaySrcset = computed<string | null>(() => {
    if (this.errored()) {
      return null;
    }
    const resolved = this.resolved();
    if (!resolved?.largeSrc) {
      return null;
    }
    return `${resolved.src} 1x, ${resolved.largeSrc} 2x`;
  });

  protected readonly displayAlt = computed<string>(() => {
    return this.resolved()?.altText ?? this.altFallback();
  });

  protected onError(): void {
    this.errored.set(true);
  }
}

App.directive('ltResolvedImage', downgradeComponent({ component: LtResolvedImageComponent }));
